// Web push for همیان وب: subscribing this browser, keeping it subscribed, and
// letting go of it on logout.
//
// The server side decides WHAT is sent (the backend mirrors the app's own
// notifications and adds a weekly follow-up) and hamyan-announce decides WHEN
// (caps, de-duplication, quiet hours, backing off from people who never tap).
// This module only owns the browser's half of the contract.
//
// Every function here is safe to call anywhere: none of them throw, and none
// of them opens the browser's permission dialog except `enableWebPush`, which
// must only run from a tap.

import type { createBrowserRouter } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
    getWebPushConfig,
    recordWebPushClick,
    subscribeWebPush,
    unsubscribeWebPush,
} from 'services/webPush'
import { queueEvent, queueRegister } from 'utilities/analytics'
import { hasSession } from 'utilities/session'
import {
    arrayBufferToUrlBase64,
    devicePlatform,
    isStandalone,
    notificationPermission,
    pushAvailability,
    readAskRecord,
    readPushOpen,
    readStoredSubscription,
    rememberAsked,
    shouldAskOnFirstOpen,
    urlBase64ToUint8Array,
    writeStoredSubscription,
} from './support'

export * from './support'

type Router = ReturnType<typeof createBrowserRouter>

// How often an already-subscribed browser tells the backend it is still alive.
// That refresh is what the CRM's «دیده‌شده در ۷ روز» counts, and it revives a
// subscription the backend switched off.
const RESYNC_MS = 24 * 60 * 60 * 1000
// In development no service worker is registered and `ready` never settles.
const SW_READY_TIMEOUT_MS = 10000
// On a browser's first visit the worker is still installing, precaching the
// whole app: that took ~25s on a fresh Chrome, well past the 10s above, and
// every tap in that window failed.
const SW_INSTALL_TIMEOUT_MS = 60000
// The browser's push service normally answers in 2-8s; past this we stop
// holding the switch and say so.
const SUBSCRIBE_TIMEOUT_MS = 45000

export type EnableResult =
    | 'subscribed'
    | 'denied' // the browser dialog was answered «Block», now or earlier
    | 'dismissed' // the browser dialog was closed without an answer
    | 'unavailable' // not here (mini app, iOS tab, old browser) or not now (server)
    | 'failed'

// What a tap is waiting on, so the page can say it instead of just greying out.
export type EnableStage = 'permission' | 'preparing'

// `first_open`: the app asked by itself, the first time this browser opened it.
// `self_test`: the settings test found this browser unknown to the server and
// re-registered it (permission was already granted, so no dialog).
export type WebPushSource = 'first_open' | 'settings' | 'self_test'

/** `promise`'s value, or `fallback` once `ms` pass first. */
const within = <T, F>(promise: Promise<T>, ms: number, fallback: F): Promise<T | F> =>
    new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => resolve(fallback), ms)
        promise.then(
            (value) => {
                window.clearTimeout(timer)
                resolve(value)
            },
            (error) => {
                window.clearTimeout(timer)
                reject(error)
            }
        )
    })

const serviceWorkerReady = async (): Promise<ServiceWorkerRegistration | null> => {
    try {
        // Registered but not active yet: it is installing, and will get there.
        const registered = !!(await navigator.serviceWorker.getRegistration())
        return await within(
            navigator.serviceWorker.ready,
            registered ? SW_INSTALL_TIMEOUT_MS : SW_READY_TIMEOUT_MS,
            null
        )
    } catch {
        return null
    }
}

const SUBSCRIBE_TIMED_OUT = { name: 'Timeout' }

export const currentBrowserSubscription = async (): Promise<PushSubscription | null> => {
    if (pushAvailability() !== 'ok') return null
    try {
        const registration = await navigator.serviceWorker.getRegistration()
        return (await registration?.pushManager.getSubscription()) ?? null
    } catch {
        return null
    }
}

const keyOf = (subscription: PushSubscription): string | null => {
    const key = subscription.options?.applicationServerKey
    return key ? arrayBufferToUrlBase64(key) : null
}

const errorName = (error: unknown): string =>
    (error as { name?: string })?.name ||
    String((error as { response?: { status?: number } })?.response?.status ?? 'unknown')

/** Subscribe (or reuse) the browser's PushSubscription and register it with us. */
const attach = async (
    registration: ServiceWorkerRegistration,
    publicKey: string
): Promise<{ subscription: PushSubscription; renewed: boolean }> => {
    let subscription = await registration.pushManager.getSubscription()
    const stored = readStoredSubscription()
    const madeWith = subscription
        ? keyOf(subscription) ?? stored?.publicKey ?? null
        : null
    let renewed = false
    if (subscription && madeWith && madeWith !== publicKey) {
        // Made with a key announce no longer signs with: every push to it
        // would be refused by the push service.
        await subscription.unsubscribe().catch(() => undefined)
        subscription = null
        renewed = true
    }
    if (!subscription) {
        subscription = await within(
            registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            }),
            SUBSCRIBE_TIMEOUT_MS,
            null
        )
        if (!subscription) throw SUBSCRIBE_TIMED_OUT
    }

    const json = subscription.toJSON()
    await subscribeWebPush({
        endpoint: subscription.endpoint,
        keys: {
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
        },
        standalone: isStandalone(),
    })
    writeStoredSubscription({
        endpoint: subscription.endpoint,
        publicKey,
        syncedAt: Date.now(),
    })
    return { subscription, renewed }
}

/**
 * Open the browser's permission dialog.
 *
 * `opened` is false when the browser would not show it at all, which is what
 * Safari and Firefox do when the user gesture behind the call has expired —
 * told apart from «closed without answering», which looks identical from here
 * but must never be retried.
 */
const requestPermission = async (): Promise<{
    opened: boolean
    permission: NotificationPermission
}> => {
    try {
        return { opened: true, permission: await Notification.requestPermission() }
    } catch {
        try {
            // Safari before 15 only had the callback form.
            return {
                opened: true,
                permission: await new Promise<NotificationPermission>((resolve) =>
                    Notification.requestPermission(resolve)
                ),
            }
        } catch {
            return { opened: false, permission: Notification.permission }
        }
    }
}

/**
 * Turn notifications on for this browser. Call from a tap only: this is what
 * opens the browser's permission dialog.
 */
export const enableWebPush = async (
    source: WebPushSource,
    onStage: (stage: EnableStage) => void = () => {},
    // Already fetched by the caller. Everything before the dialog then runs
    // synchronously, which is what Safari and Firefox require: they refuse a
    // permission request that comes after an await, and a config round-trip on
    // a slow connection is exactly that.
    preloaded: IWebPushConfig | null = null
): Promise<EnableResult> => {
    const started = Date.now()
    const availability = pushAvailability()
    if (availability !== 'ok') {
        queueEvent('web_push_unavailable', { source, reason: availability })
        return 'unavailable'
    }

    let config: IWebPushConfig
    try {
        config = preloaded ?? (await getWebPushConfig())
    } catch (error) {
        queueEvent('web_push_subscribe_failed', {
            source,
            stage: 'config',
            error: errorName(error),
        })
        return 'failed'
    }
    if (!config.enabled || !config.public_key) {
        queueEvent('web_push_unavailable', { source, reason: 'server' })
        return 'unavailable'
    }

    const before = Notification.permission
    let permission: NotificationPermission = before
    if (permission !== 'granted') {
        queueEvent('web_push_permission_requested', { source, before })
        onStage('permission')
        const asked = await requestPermission()
        if (!asked.opened) {
            // The browser refused to even open the dialog — on Safari and
            // Firefox that means the tap behind this call has gone cold. No
            // answer was given, so none is recorded: the next tap opens it.
            queueEvent('web_push_permission_refused', { source })
            return 'unavailable'
        }
        permission = asked.permission
        queueEvent('web_push_permission_result', {
            source,
            result: permission,
        })
    }
    queueRegister({ web_push_permission: permission })
    if (permission === 'denied') return 'denied'
    if (permission !== 'granted') return 'dismissed'

    onStage('preparing')
    const registration = await serviceWorkerReady()
    if (!registration) {
        queueEvent('web_push_subscribe_failed', {
            source,
            stage: 'service_worker',
            ms: Date.now() - started,
        })
        return 'failed'
    }

    try {
        const { renewed } = await attach(registration, config.public_key)
        queueRegister({ web_push: 'on' })
        queueEvent('web_push_subscribed', {
            source,
            renewed,
            permission_before: before,
            standalone: isStandalone(),
            devices_before: config.active_devices,
            ms: Date.now() - started,
        })
        return 'subscribed'
    } catch (error) {
        queueEvent('web_push_subscribe_failed', {
            source,
            stage: 'subscribe',
            error: errorName(error),
            ms: Date.now() - started,
        })
        return 'failed'
    }
}

/** Turn notifications off for this browser only (the settings switch). */
export const disableWebPush = async (): Promise<boolean> => {
    const subscription = await currentBrowserSubscription()
    const endpoint = subscription?.endpoint ?? readStoredSubscription()?.endpoint
    try {
        if (endpoint) await unsubscribeWebPush(endpoint, 'unsubscribed')
    } catch (error) {
        queueEvent('web_push_unsubscribe_failed', { error: errorName(error) })
        return false
    }
    await subscription?.unsubscribe().catch(() => undefined)
    writeStoredSubscription(null)
    queueRegister({ web_push: 'off' })
    queueEvent('web_push_unsubscribed', { reason: 'settings' })
    return true
}

/** Is this browser currently receiving our notifications? */
export const isWebPushOn = (): boolean =>
    pushAvailability() === 'ok' &&
    notificationPermission() === 'granted' &&
    !!readStoredSubscription()

/**
 * Logout: the next person on this browser must not get the last one's
 * reminders.
 *
 * Must be called BEFORE the session is cleared. Everything it needs from
 * localStorage is read synchronously on the way in; the requests themselves
 * finish in the background.
 */
export const releaseWebPushOnLogout = () => {
    const stored = readStoredSubscription()
    if (!stored) return
    let token: string | undefined
    try {
        token = JSON.parse(window.localStorage.getItem('user') || 'null')?.token
    } catch {
        token = undefined
    }
    writeStoredSubscription(null)
    if (token) {
        unsubscribeWebPush(stored.endpoint, 'logout', token).catch(() => undefined)
    }
    currentBrowserSubscription()
        .then((subscription) => subscription?.unsubscribe())
        .catch(() => undefined)
}

/**
 * Keep an opted-in browser subscribed. Runs once per app start, in the
 * background, and never asks for anything.
 */
export const syncWebPush = async (): Promise<void> => {
    if (!hasSession() || pushAvailability() !== 'ok') return

    const permission = Notification.permission
    queueRegister({ web_push_permission: permission })
    const stored = readStoredSubscription()

    if (permission !== 'granted') {
        if (stored) {
            // Switched off from the browser's own site settings.
            writeStoredSubscription(null)
            queueRegister({ web_push: 'off' })
            queueEvent('web_push_permission_revoked', { permission })
        }
        return
    }

    const subscription = await currentBrowserSubscription()

    if (!stored) {
        if (subscription) {
            // Left behind by a session that ended without logging out (it
            // expired). We cannot tell whose it was, so it goes: whoever is
            // logged in now gets their own from `installWebPushFirstOpen`
            // without a dialog, and nobody receives someone else's
            // notifications in the meantime.
            await subscription.unsubscribe().catch(() => undefined)
            queueEvent('web_push_orphan_released', {})
        }
        queueRegister({ web_push: 'off' })
        return
    }

    queueRegister({ web_push: 'on' })
    const due = Date.now() - stored.syncedAt > RESYNC_MS
    const moved = !subscription || subscription.endpoint !== stored.endpoint
    if (!due && !moved) return

    try {
        const config = await getWebPushConfig()
        // announce is down or unconfigured: keep what we have and try tomorrow.
        if (!config.enabled || !config.public_key) return
        const registration = await serviceWorkerReady()
        if (!registration) return
        const { renewed } = await attach(registration, config.public_key)
        if (moved || renewed) {
            queueEvent('web_push_resubscribed', {
                reason: renewed ? 'key_rotated' : subscription ? 'endpoint_changed' : 'lost',
            })
        }
    } catch (error) {
        queueEvent('web_push_sync_failed', { error: errorName(error) })
    }
}

// ── the first open ─────────────────────────────────────────────────────────
//
// There is no card and no «فعال‌سازی» button of ours any more: the first time
// someone opens the app we open the browser's own dialog, and that is the
// whole ask. Once per browser (`rememberAsked`), never after a «Block», and
// never in a mini app or an iPhone tab where a push could not arrive anyway.
//
// It still waits for a tap. Not out of politeness — Safari and Firefox refuse
// a permission request that is not tied to a live gesture, and Chrome demotes
// sites that ask into a silent icon in the address bar. In practice that tap
// is the one they arrive with: someone who just signed in already carries user
// activation, so the dialog opens as the app does.

// The earliest events that count as a gesture. `pointerdown` covers mouse and
// touch on anything current; `touchend` is for browsers without pointer events.
const GESTURES: Array<'pointerdown' | 'keydown' | 'touchend'> = [
    'pointerdown',
    'keydown',
    'touchend',
]

const hasUserActivation = (): boolean => {
    try {
        return !!(
            navigator as Navigator & {
                userActivation?: { hasBeenActive?: boolean }
            }
        ).userActivation?.hasBeenActive
    } catch {
        return false
    }
}

let firstOpenInstalled = false
// Fetched before the dialog, never during it: `enableWebPush` is handed this
// so that everything between the tap and the dialog is synchronous.
let preloadedConfig: IWebPushConfig | null = null
let configRequest: Promise<IWebPushConfig | null> | null = null

const preloadConfig = (): Promise<IWebPushConfig | null> => {
    if (!configRequest) {
        configRequest = getWebPushConfig()
            .then((config) => {
                preloadedConfig = config
                return config
            })
            .catch(() => {
                // Signed out, or announce is unreachable: let the next tap try.
                configRequest = null
                return null
            })
    }
    return configRequest
}

/**
 * Open the dialog and remember the answer.
 *
 * @returns whether an answer was given. `false` means no dialog was ever
 * shown, so nothing is recorded and the next tap may try again.
 */
const askOnFirstOpen = async (
    trigger: 'activation' | 'gesture'
): Promise<boolean> => {
    queueEvent('web_push_first_open_ask', {
        trigger,
        platform: devicePlatform(),
        standalone: isStandalone(),
    })
    const result = await enableWebPush('first_open', () => {}, preloadedConfig)
    if (result === 'failed' || result === 'unavailable') return false
    rememberAsked(result === 'subscribed' ? 'granted' : result)
    if (result === 'subscribed') {
        toast.success(
            'اعلان‌های همیان روشن شد. از منوی «اعلان‌ها» هر وقت خواستید تغییرش بدهید.'
        )
    }
    return true
}

/**
 * Ask this browser for notification permission, once, on the first open — and
 * keep a browser that already said yes subscribed.
 *
 * Safe to call at startup before anyone has signed in: it waits for a session
 * (the taps on the login screen are not ours to spend) and does nothing at all
 * where push cannot arrive.
 */
export const installWebPushFirstOpen = () => {
    if (firstOpenInstalled) return
    firstOpenInstalled = true
    if (pushAvailability() !== 'ok') return

    let busy = false

    /** @returns whether this browser is settled and nothing is left to do. */
    const attempt = async (
        trigger: 'activation' | 'gesture'
    ): Promise<boolean> => {
        const permission = notificationPermission()
        if (permission === 'granted') {
            // Allowed already, so no dialog is involved and this can be
            // silent: it is how a browser whose subscription was dropped
            // (announce rotated its key, an expired session released it) comes
            // back without anyone having to find the switch.
            if (!isWebPushOn())
                await enableWebPush('first_open', () => {}, preloadedConfig)
            return true
        }
        if (
            !shouldAskOnFirstOpen(pushAvailability(), permission, readAskRecord())
        )
            return true
        if (!preloadedConfig) {
            // A tap before the config arrived — right after signing in, say.
            // Chrome does not mind the wait; a browser that does simply shows
            // nothing, records nothing, and gets asked again on the next tap,
            // which by then is synchronous.
            if (!(await preloadConfig())) return false
        }
        return askOnFirstOpen(trigger)
    }

    const listen = () =>
        GESTURES.forEach((type) =>
            window.addEventListener(type, onGesture, {
                capture: true,
                passive: true,
            })
        )
    const stop = () =>
        GESTURES.forEach((type) =>
            window.removeEventListener(type, onGesture, true)
        )

    function onGesture() {
        // Still signed out: keep waiting rather than asking a stranger — and
        // rather than spending the ask on a config request that would 401.
        if (!hasSession() || busy) return
        // Only one dialog at a time, however fast they tap.
        busy = true
        void attempt('gesture').then(
            (done) => {
                busy = false
                if (done) stop()
            },
            () => {
                busy = false
            }
        )
    }

    if (!hasSession()) {
        listen()
        return
    }
    if (notificationPermission() === 'granted') {
        void attempt('activation')
        return
    }
    if (
        !shouldAskOnFirstOpen(
            pushAvailability(),
            notificationPermission(),
            readAskRecord()
        )
    )
        return

    void preloadConfig().then((config) => {
        // `hasBeenActive` covers the tap they signed in with. It is sticky, so
        // it can be true while the browser's own (transient) window has
        // closed; then the dialog does not open, nothing is recorded, and the
        // listener picks it up on the very next tap.
        if (config && hasUserActivation()) {
            void attempt('activation').then((done) => {
                if (!done) listen()
            })
            return
        }
        listen()
    })
}

const trackOpen = (
    messageId: number | null,
    category: string,
    cold: boolean
) => {
    queueEvent('web_push_clicked', {
        message_id: messageId,
        category,
        cold_start: cold,
    })
    if (messageId && hasSession()) {
        recordWebPushClick(messageId).catch(() => undefined)
    }
}

// Read at import time: the redirects at startup replace the URL before
// anything else gets to look at it.
const launchOpen =
    typeof window !== 'undefined'
        ? readPushOpen(window.location.pathname, window.location.search)
        : null

/**
 * Count notification taps and route them.
 *
 * Two ways in. With the app closed, the service worker opens the page with
 * `?pn=&pc=` and we strip them here. With the app already open, it focuses that
 * window and posts `WEB_PUSH_OPEN`, and we navigate inside the SPA instead of
 * reloading it.
 */
export const installWebPushOpenHandling = (
    router: Router,
    redirected: boolean
) => {
    if (launchOpen) {
        trackOpen(launchOpen.messageId, launchOpen.category, true)
        if (!redirected) router.navigate(launchOpen.cleanPath, { replace: true })
    }

    if (!('serviceWorker' in navigator)) return
    navigator.serviceWorker.addEventListener('message', (event) => {
        const data = event.data
        if (!data || data.type !== 'WEB_PUSH_OPEN') return
        let target: URL
        try {
            target = new URL(String(data.url), window.location.origin)
        } catch {
            return
        }
        if (target.origin !== window.location.origin) return
        const open = readPushOpen(target.pathname, target.search)
        if (!open) return
        trackOpen(open.messageId, open.category, false)
        router.navigate(open.cleanPath)
    })
}
